/**
 * `NarrativeBackdrop` — the world the GM pinned behind a narrative scene (`narrative-backdrop`): a
 * huge hero name, the codex designation lines and the spinning procedural planet (`PlanetStage`). Every line comes from the served codex entry
 * (`backdropReadout`), which every player can already read. The planet's look, and the layer's colour
 * theme with it (`data-planet`), is the entry's profile (`planetProfiles.ts`). The owner keys this
 * component by entry id, so a new pin builds a new stage.
 *
 * The layer is `position: fixed` over the whole viewport, under the strip, rail pages and COMMS, so
 * panes open and close over it and the strip resizes beside it without the planet moving. Its type is
 * placed against the shell's fixed columns so it stays clear of a narrow page and of COMMS.
 *
 * The render loop runs only while the layer can be seen: `covered` (a wide page over the stage)
 * pauses it, a hidden tab pauses it, and unmounting — the table leaving narrative, or the backdrop
 * being cleared — disposes every GL resource.
 *
 * A play/pause icon button sets how the planet idles, per viewer (remembered in this browser): pause, the
 * default, lets it settle and stop drawing after a drag; play keeps the slow spin. It is absent
 * wherever the planet doesn't animate anyway (reduced motion, no WebGL, a renderer too slow).
 *
 * Each stage gets a canvas of its own, made in the effect: disposing forces the context lost, and a
 * canvas hands back that same dead context forever, so a remount on the old canvas (StrictMode's
 * dev double-mount) drew nothing.
 */
import { useEffect, useRef, useState } from "react";
import { faPause, faPlay } from "@fortawesome/free-solid-svg-icons";
import { backdropReadout, type CodexEntry } from "@lancer-vtt/domain";
import { storageGet, storageSet } from "../../../../lib/browserStorage";
import { Icon } from "../../../../lib/Icon";
import { prefersReducedMotion } from "../../../../lib/prefersReducedMotion";
import { useStage } from "../stageContext";
import { PlanetStage } from "./PlanetStage";
import { planetProfile } from "./planetProfiles";
import type { SpinMode } from "./spin";
import "./backdrop.css";

/** The viewer's play/pause preference. Anything but `"play"`, or an unreachable store, is pause. */
const MOTION_KEY = "lvtt.backdropMotion";
function readMotion(): SpinMode {
  const stored = storageGet(MOTION_KEY);
  return stored.ok && stored.value === "play" ? "play" : "pause";
}

export function NarrativeBackdrop({ entry, covered }: { entry: CodexEntry; covered: boolean }) {
  const readout = backdropReadout(entry);
  const profile = planetProfile(entry.id);
  const hostRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<PlanetStage | null>(null);
  const [noGl, setNoGl] = useState(false);
  const [grabbing, setGrabbing] = useState(false);
  const [mode, setMode] = useState(readMotion);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  // Still mode (reduced motion, or a renderer measured too slow) has no motion to play or pause.
  const [still, setStill] = useState(prefersReducedMotion);
  const sceneReady = useStage()?.sceneReady;

  useEffect(() => {
    const host = hostRef.current!;
    const canvas = document.createElement("canvas");
    host.appendChild(canvas);
    let stage: PlanetStage;
    try {
      stage = new PlanetStage(canvas, { profile, reducedMotion: prefersReducedMotion(), mode: modeRef.current, onDegrade: () => setStill(true) });
    } catch (e) {
      console.warn("narrative backdrop: no WebGL context, showing the type alone", e);
      canvas.remove();
      setNoGl(true);
      sceneReady?.();
      return;
    }
    stageRef.current = stage;
    const fit = () => stage.setSize(window.innerWidth, window.innerHeight, window.devicePixelRatio);
    fit();
    window.addEventListener("resize", fit);
    Object.defineProperty(window, "__backdropDebug", { configurable: true, get: () => stage.debug() });
    // The scene cut's ready signal: the render loop has drawn a frame within two.
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => sceneReady?.());
    });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      delete window.__backdropDebug;
      stageRef.current = null;
      stage.dispose();
      canvas.remove();
    };
    // One stage per mount; `sceneReady` is stable for the shell's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    stageRef.current?.setRunning(!covered);
  }, [covered, noGl]);

  useEffect(() => {
    stageRef.current?.setMode(mode);
  }, [mode]);

  const motionLabel = mode === "play" ? "Pause planet motion" : "Play planet motion";
  const toggleMode = () => {
    const next = mode === "play" ? "pause" : "play";
    storageSet(MOTION_KEY, next);
    setMode(next);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    stageRef.current?.pointerDown(e.nativeEvent);
    setGrabbing(true);
  };
  const onPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    stageRef.current?.pointerUp(e.nativeEvent);
    setGrabbing(false);
  };

  return (
    <div className="nb" role="region" aria-label={`Backdrop: ${readout.name}`} data-backdrop={entry.id} data-planet={profile.key}>
      <div
        ref={hostRef}
        className={`nb-canvas${grabbing ? " nb-canvas--grabbing" : ""}`}
        hidden={noGl}
        aria-hidden="true"
        onPointerDown={onPointerDown}
        onPointerMove={(e) => stageRef.current?.pointerMove(e.nativeEvent)}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      />
      <div className="nb-shade" aria-hidden="true" />
      <header className="nb-hero">
        <div className="nb-kicker">
          <span>CODEX</span>
          <span className="nb-kicker-rule" />
        </div>
        <h1 className="nb-name">{readout.name}</h1>
        {readout.designation !== null && <div className="nb-designation">{readout.designation}</div>}
        {readout.details.length > 0 && (
          <div className="nb-details">
            {readout.details.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
        )}
        {!noGl && !still && (
          <button
            type="button"
            className="nb-motion"
            aria-pressed={mode === "play"}
            aria-label={motionLabel}
            onClick={toggleMode}
          >
            <Icon icon={mode === "play" ? faPause : faPlay} />
          </button>
        )}
      </header>
    </div>
  );
}
